import { useState, useCallback, useRef } from 'react';
import { chatbotApi, RuleQuestion } from './chatbotApi';

export interface Message {
  id: string;
  role: 'user' | 'bot';
  text: string;
  quickReplies?: QuickReply[];
}

export interface QuickReply {
  label: string;
  value: string;
  questionId?: number;
}

type ChatState =
  | 'idle'
  | 'showing_main_menu'
  | 'showing_questions'
  | 'awaiting_free_text'
  | 'awaiting_email'
  | 'awaiting_ticket_type'
  | 'awaiting_ticket_description';

const generateId = () => Math.random().toString(36).slice(2, 10);

export function useChatFlow() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isInputDisabled, setIsInputDisabled] = useState(false);
  const [chatState, setChatState] = useState<ChatState>('idle');
  const sessionIdRef = useRef(crypto.randomUUID?.() || generateId());
  const lastQuestionRef = useRef<{ id: number; text: string } | null>(null);
  const ticketTypeRef = useRef<string>('');
  const ticketDescriptionRef = useRef<string>('');

  const addBotMessage = useCallback((text: string, quickReplies?: QuickReply[]) => {
    setMessages(prev => [...prev, { id: generateId(), role: 'bot', text, quickReplies }]);
  }, []);

  const addUserMessage = useCallback((text: string) => {
    setMessages(prev => [...prev, { id: generateId(), role: 'user', text }]);
  }, []);

  const showMainMenu = useCallback(() => {
    addBotMessage('Hi there! How can I help you today?', [
      { label: '🎫 Support Ticket', value: '__create_ticket__' },
      { label: '📋 Main Menu', value: '__main_menu__' },
    ]);
    setChatState('showing_main_menu');
    setIsInputDisabled(true);
  }, [addBotMessage]);

  const loadQuestions = useCallback(async () => {
    setIsTyping(true);
    setIsInputDisabled(true);

    try {
      const res = await chatbotApi.getRootQuestions();
      const questions = res?.questions || [];
      setIsTyping(false);

      if (questions.length > 0) {
        const quickReplies: QuickReply[] = questions.map((q: RuleQuestion) => ({
          label: q.question_text,
          value: q.question_text,
          questionId: q.id,
        }));
        quickReplies.push({ label: '💬 Type your own question', value: '__free_text__' });
        quickReplies.push({ label: '↩️ Back', value: '__back_to_start__' });

        addBotMessage('Please choose a topic:', quickReplies);
        setChatState('showing_questions');
        setIsInputDisabled(true);
      } else {
        addBotMessage('Type your question below and I\'ll try to find an answer for you.');
        setChatState('awaiting_free_text');
        setIsInputDisabled(false);
      }
    } catch (err) {
      console.error('Failed to load questions:', err);
      setIsTyping(false);
      addBotMessage('Type your question below and I\'ll try to find an answer for you.');
      setChatState('awaiting_free_text');
      setIsInputDisabled(false);
    }
  }, [addBotMessage]);

  const handleQuestionClick = useCallback(async (questionId: number, questionText: string) => {
    addUserMessage(questionText);
    lastQuestionRef.current = { id: questionId, text: questionText };
    setIsTyping(true);
    setIsInputDisabled(true);

    try {
      const raw = await chatbotApi.sendMessage(sessionIdRef.current, questionId);
      setIsTyping(false);

      const records = raw?.records || [];
      const nextQuestions = raw?.nextQuestions || [];

      for (const record of records) {
        if (record.message_type === 'BOT') {
          if (nextQuestions.length > 0) {
            const replies: QuickReply[] = nextQuestions.map((q: RuleQuestion) => ({
              label: q.question_text,
              value: q.question_text,
              questionId: q.id,
            }));
            replies.push({ label: '↩️ Back', value: '__back_to_start__' });
            addBotMessage(record.message_text, replies);
            setIsInputDisabled(true);
          } else {
            addBotMessage(record.message_text);
            addBotMessage('Is there anything else I can help with?', [
              { label: '🎫 Support Ticket', value: '__create_ticket__' },
              { label: '📋 Main Menu', value: '__main_menu__' },
            ]);
            setIsInputDisabled(true);
          }
        }
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setIsTyping(false);
      addBotMessage('Sorry, something went wrong. Please try again.', [
        { label: '📋 Main Menu', value: '__main_menu__' },
      ]);
      setIsInputDisabled(true);
    }
  }, [addUserMessage, addBotMessage]);

  const handleFreeText = useCallback(async (text: string) => {
    addUserMessage(text);
    setIsTyping(true);
    setIsInputDisabled(true);

    try {
      const res = await chatbotApi.askQuestion(text, sessionIdRef.current);
      setIsTyping(false);

      const data = res?.data;

      if (!data) {
        addBotMessage('Sorry, something went wrong. Please try again.', [
          { label: '📋 Main Menu', value: '__main_menu__' },
        ]);
        setIsInputDisabled(true);
        return;
      }

      if (data.ticket_created) {
        addBotMessage(data.message || `Your ticket has been submitted: ${data.ticket_number}. Our team will get back to you.`);
        addBotMessage('Is there anything else?', [
          { label: '🎫 Support Ticket', value: '__create_ticket__' },
          { label: '📋 Main Menu', value: '__main_menu__' },
        ]);
        setIsInputDisabled(true);
        setChatState('showing_main_menu');

      } else if (data.needs_ticket) {
        const prompt = data.message || "I couldn't find a matching answer. Would you like to submit a support ticket?";
        addBotMessage(prompt, [
          { label: '🎫 Submit a ticket', value: '__create_ticket__' },
          { label: '📋 Main Menu', value: '__main_menu__' },
        ]);
        setIsInputDisabled(true);
        setChatState('showing_main_menu');

      } else if (data.answer) {
        addBotMessage(data.answer);
        addBotMessage('Was this helpful?', [
          { label: '✅ Yes, thanks!', value: '__end__' },
          { label: '🎫 Support Ticket', value: '__create_ticket__' },
          { label: '📋 Main Menu', value: '__main_menu__' },
        ]);
        setIsInputDisabled(true);
        setChatState('showing_main_menu');
      }
    } catch (err) {
      console.error('Ask failed:', err);
      setIsTyping(false);
      addBotMessage('Sorry, something went wrong. Please try again.', [
        { label: '📋 Main Menu', value: '__main_menu__' },
      ]);
      setIsInputDisabled(true);
    }
  }, [addUserMessage, addBotMessage]);

  /** Step 3: Collect email and create ticket */
  const handleEmailSubmit = useCallback(async (email: string) => {
    addUserMessage(email);
    setIsTyping(true);
    setIsInputDisabled(true);

    const description = ticketDescriptionRef.current || lastQuestionRef.current?.text || 'User submitted a question via chatbot';
    const type = ticketTypeRef.current || 'issue';

    try {
      const res = await chatbotApi.createTicket(
        sessionIdRef.current,
        email,
        type,
        description,
      );
      setIsTyping(false);

      if (res?.success && res.data) {
        addBotMessage(`Your ticket has been created: **${res.data.ticket_number}**. We'll get back to you at ${email}.`);
      } else {
        addBotMessage(`Thanks! We've noted your email (${email}). Our team will review your question and get back to you.`);
      }

      addBotMessage('Is there anything else?', [
        { label: '🎫 Support Ticket', value: '__create_ticket__' },
        { label: '📋 Main Menu', value: '__main_menu__' },
      ]);
      setIsInputDisabled(true);
      setChatState('showing_main_menu');
    } catch (err) {
      console.error('Ticket creation failed:', err);
      setIsTyping(false);
      addBotMessage(`Thanks! We've noted your email (${email}). Our team will review your question and get back to you.`);
      addBotMessage('Is there anything else?', [
        { label: '🎫 Support Ticket', value: '__create_ticket__' },
        { label: '📋 Main Menu', value: '__main_menu__' },
      ]);
      setIsInputDisabled(true);
      setChatState('showing_main_menu');
    }
  }, [addUserMessage, addBotMessage]);

  /** Handle quick reply button clicks */
  const handleQuickReply = useCallback((reply: QuickReply) => {
    if (reply.value === '__main_menu__') {
      addUserMessage('Main Menu');
      loadQuestions();
      return;
    }

    if (reply.value === '__back_to_start__') {
      addUserMessage('Back');
      showMainMenu();
      return;
    }

    if (reply.value === '__free_text__') {
      addUserMessage('I want to ask something else');
      addBotMessage('Type your question below and I\'ll try to find an answer for you.');
      setChatState('awaiting_free_text');
      setIsInputDisabled(false);
      return;
    }

    if (reply.value === '__create_ticket__') {
      addUserMessage('Support Ticket');
      addBotMessage('What type of request is this?', [
        { label: '🐛 Issue', value: '__ticket_type_issue__' },
        { label: '💡 Feature Request', value: '__ticket_type_new_feature__' },
        { label: '🔧 Enhancement', value: '__ticket_type_enhancement__' },
      ]);
      setChatState('awaiting_ticket_type');
      setIsInputDisabled(true);
      return;
    }

    if (reply.value.startsWith('__ticket_type_')) {
      const type = reply.value.replace('__ticket_type_', '').replace('__', '');
      ticketTypeRef.current = type;
      addUserMessage(reply.label);
      addBotMessage('Please describe your issue or request briefly:');
      setChatState('awaiting_ticket_description');
      setIsInputDisabled(false);
      return;
    }

    if (reply.value === '__end__') {
      addUserMessage('Yes, thanks!');
      addBotMessage('Glad I could help! Feel free to come back anytime. 👋');
      showMainMenu();
      return;
    }

    if (reply.questionId) {
      handleQuestionClick(reply.questionId, reply.label);
    }
  }, [addUserMessage, addBotMessage, loadQuestions, showMainMenu, handleQuestionClick]);

  /** Handle text input submission */
  const handleUserInput = useCallback((text: string) => {
    if (!text.trim()) return;

    if (chatState === 'awaiting_ticket_description') {
      ticketDescriptionRef.current = text.trim();
      addUserMessage(text.trim());
      addBotMessage('Enter your email address so we can get back to you:');
      setChatState('awaiting_email');
      setIsInputDisabled(false);
      return;
    }

    if (chatState === 'awaiting_email') {
      if (text.includes('@') && text.includes('.')) {
        handleEmailSubmit(text.trim());
      } else {
        addBotMessage('Please enter a valid email address.');
      }
      return;
    }

    if (chatState === 'awaiting_free_text' || chatState === 'showing_questions') {
      handleFreeText(text.trim());
      return;
    }
  }, [chatState, handleFreeText, handleEmailSubmit, addUserMessage, addBotMessage]);

  const startConversation = useCallback(() => {
    setMessages([]);
    setIsTyping(false);
    setIsInputDisabled(false);
    setChatState('idle');
    sessionIdRef.current = crypto.randomUUID?.() || generateId();
    lastQuestionRef.current = null;
    ticketTypeRef.current = '';
    ticketDescriptionRef.current = '';
    showMainMenu();
  }, [showMainMenu]);

  const resetConversation = useCallback(() => {
    startConversation();
  }, [startConversation]);

  return {
    messages,
    isTyping,
    isInputDisabled,
    chatState,
    handleQuickReply,
    handleUserInput,
    startConversation,
    resetConversation,
  };
}
